import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Plus, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Key,
  FileKey,
  Search,
  Filter
} from 'lucide-react';
import { certificateApi } from '../../services/iso20022Api';
import { 
  formatDate, 
  formatRelativeTime, 
  getStatusBadgeClass 
} from '../../utils/helpers';

const Certificates = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [certsData, summaryData] = await Promise.all([
        certificateApi.getCertificates(filters),
        certificateApi.getStatusSummary()
      ]);
      
      setCertificates(certsData);
      setSummary(summaryData);
      setError(null);
    } catch (err) {
      console.error('Failed to load certificates:', err);
      setError('Failed to load certificate data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadData();
  };

  const getCertificateStatusBadge = (status) => {
    const statusMap = {
      'ACTIVE': 'badge-success',
      'PENDING': 'badge-warning',
      'REVOKED': 'badge-danger',
      'EXPIRED': 'badge-danger'
    };
    return statusMap[status] || 'badge-info';
  };

  const getCertificateTypeBadge = (type) => {
    const typeMap = {
      'SELF_SIGNED': 'badge bg-blue-100 text-blue-800',
      'CA_SIGNED': 'badge bg-purple-100 text-purple-800',
      'IMPORTED': 'badge bg-gray-100 text-gray-800'
    };
    return typeMap[type] || 'badge-info';
  };

  if (loading && !certificates.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Loading certificates...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
            <Shield className="w-8 h-8 text-primary-600" />
            <span>Certificate Management</span>
          </h1>
          <p className="text-gray-600 mt-1">
            Manage signing certificates and certificate lifecycle
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            className="btn-secondary flex items-center space-x-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => navigate('/iso20022/certificates/generate')}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Generate Certificate</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Certificates</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {summary.totalCertificates}
                  </p>
                </div>
                <Key className="w-8 h-8 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active</p>
                  <p className="text-2xl font-bold text-success-600 mt-1">
                    {summary.activeCertificates}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-success-400" />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
                  <p className="text-2xl font-bold text-warning-600 mt-1">
                    {summary.expiringSoon}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-warning-400" />
              </div>
              {summary.expiringSoonCritical > 0 && (
                <p className="text-xs text-warning-600 mt-2">
                  {summary.expiringSoonCritical} critical (&lt;7 days)
                </p>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Signatures</p>
                  <p className="text-2xl font-bold text-primary-600 mt-1">
                    {summary.totalSignatures?.toLocaleString() || 0}
                  </p>
                </div>
                <FileKey className="w-8 h-8 text-primary-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Primary Certificate Alert */}
      {summary?.primaryCertificateAlias && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Primary Signing Certificate: <span className="font-mono">{summary.primaryCertificateAlias}</span>
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {summary.primaryCertificateDaysRemaining} days until expiry
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/iso20022/certificates/${summary.primaryCertificateId}`)}
              className="text-sm text-blue-700 hover:text-blue-900 font-medium"
            >
              View Details →
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  className="input pl-10"
                  placeholder="Alias, subject..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="PENDING">Pending</option>
                <option value="REVOKED">Revoked</option>
                <option value="EXPIRED">Expired</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                className="input"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="SELF_SIGNED">Self-Signed</option>
                <option value="CA_SIGNED">CA-Signed</option>
                <option value="IMPORTED">Imported</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={applyFilters}
                className="btn-primary w-full flex items-center justify-center space-x-2"
              >
                <Filter className="w-4 h-4" />
                <span>Apply Filters</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Certificates Table */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Certificates</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alias
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {certificates.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No certificates found</p>
                    <button
                      onClick={() => navigate('/iso20022/certificates/generate')}
                      className="btn-primary mt-4"
                    >
                      Generate Your First Certificate
                    </button>
                  </td>
                </tr>
              ) : (
                certificates.map((cert) => (
                  <tr
                    key={cert.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/iso20022/certificates/${cert.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {cert.isPrimary && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        )}
                        <span className="text-sm font-medium text-gray-900 font-mono">
                          {cert.alias}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={cert.subject}>
                        {cert.subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getCertificateTypeBadge(cert.certificateType)}>
                        {cert.certificateType?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getCertificateStatusBadge(cert.status)}>
                        {cert.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className={`font-medium ${
                          cert.isExpired ? 'text-red-600' :
                          cert.isExpiringSoon ? 'text-warning-600' :
                          'text-gray-900'
                        }`}>
                          {cert.daysUntilExpiry} days
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(cert.notAfter, 'PP')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {cert.usageCount?.toLocaleString() || 0}
                      </div>
                      {cert.lastUsedAt && (
                        <div className="text-xs text-gray-500">
                          {formatRelativeTime(cert.lastUsedAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/iso20022/certificates/${cert.id}`);
                        }}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Certificates;
